import { useCallback, useEffect, useRef, useState } from 'react';

const UNDO_WINDOW_SECONDS = 5;

/**
 * Check-in otimista com janela de cancelamento.
 *
 * A linha muda para "entrou" na hora e a requisição só é enviada depois de
 * `UNDO_WINDOW_SECONDS`. Isso troca os 2 cliques do modal de confirmação por 1
 * clique, sem perder a rede de proteção — e o desfazer fica na própria linha,
 * onde o olho já está.
 *
 * A janela existe porque a API não tem endpoint para reverter um check-in:
 * depois de enviado, é definitivo. Cancelar antes do envio é a única forma
 * honesta de oferecer "desfazer".
 *
 * Cada participante tem seu próprio timer, então check-ins em sequência numa
 * fila não atrapalham uns aos outros.
 */
export default function usePendingCheckin({ onCommit, onError }) {
    const [pending, setPending] = useState({});
    const timersRef = useRef({});

    const clearTimers = useCallback((code) => {
        const entry = timersRef.current[code];
        if (!entry) return;

        clearTimeout(entry.commitTimer);
        clearInterval(entry.tickTimer);
        delete timersRef.current[code];
    }, []);

    // Limpa timers pendentes se a tela for desmontada no meio da janela.
    useEffect(() => {
        return () => {
            Object.keys(timersRef.current).forEach((code) => {
                const entry = timersRef.current[code];
                clearTimeout(entry.commitTimer);
                clearInterval(entry.tickTimer);
            });
            timersRef.current = {};
        };
    }, []);

    const schedule = useCallback(
        (participant) => {
            const code = participant?.code;
            if (!code || timersRef.current[code]) return;

            setPending((prev) => ({
                ...prev,
                [code]: { participant, secondsLeft: UNDO_WINDOW_SECONDS },
            }));

            const tickTimer = setInterval(() => {
                setPending((prev) => {
                    const current = prev[code];
                    if (!current) return prev;

                    return {
                        ...prev,
                        [code]: { ...current, secondsLeft: Math.max(current.secondsLeft - 1, 0) },
                    };
                });
            }, 1000);

            const commitTimer = setTimeout(async () => {
                clearTimers(code);

                try {
                    await onCommit(participant);
                } catch (error) {
                    onError?.(participant, error);
                } finally {
                    // Só sai do estado pendente depois do envio, para a linha não
                    // piscar de volta para "Ativo" antes da lista ser recarregada.
                    setPending((prev) => {
                        const next = { ...prev };
                        delete next[code];
                        return next;
                    });
                }
            }, UNDO_WINDOW_SECONDS * 1000);

            timersRef.current[code] = { commitTimer, tickTimer };
        },
        [clearTimers, onCommit, onError]
    );

    const cancel = useCallback(
        (participant) => {
            const code = participant?.code;
            if (!code) return;

            clearTimers(code);

            setPending((prev) => {
                const next = { ...prev };
                delete next[code];
                return next;
            });
        },
        [clearTimers]
    );

    /** Envia imediatamente tudo que ainda está na janela — usado ao sair da lista. */
    const flush = useCallback(() => {
        Object.values(pending).forEach(({ participant }) => {
            const code = participant?.code;
            if (!timersRef.current[code]) return;

            clearTimers(code);
            Promise.resolve(onCommit(participant)).catch((error) =>
                onError?.(participant, error)
            );
        });

        setPending({});
    }, [pending, clearTimers, onCommit, onError]);

    return {
        pending,
        schedule,
        cancel,
        flush,
        hasPending: Object.keys(pending).length > 0,
    };
}

export { UNDO_WINDOW_SECONDS };
